import Typography from "Components/Atoms/Typography";
import Modal from "Components/Molecules/Modal";
import React from "react";
import { ReservationShift } from "types/shared/helper/shifts";
import ShiftBlockTimeline, {
  SegmentState,
  useShiftSegments,
} from "App/ReservationBook/Components/ShiftBlockTimeline";

type Props = {
  open: boolean;
  onClose: () => void;
  spaceId: string;
  shifts: ReservationShift[];
  loading: boolean;
  onToggle: (closed: boolean, shiftIds?: string[]) => void;
};

const closedByOtherRules = (shift: ReservationShift, spaceId: string) =>
  !!shift.closed ||
  (!!shift.spaces?.length &&
    !shift.spaces.includes(spaceId) &&
    !!shift.matchBoth) ||
  !!shift.overwrite?.blockReservations;

const SpaceBlockModal: React.FC<Props> = ({
  open,
  onClose,
  spaceId,
  shifts,
  loading,
  onToggle,
}) => {
  const { segments, range } = useShiftSegments(shifts, shifts);

  const isClosedBySpace = (shift: ReservationShift) =>
    !!shift.overwrite?.closedSpaces?.includes(spaceId);

  const isClosed = (shift: ReservationShift) =>
    closedByOtherRules(shift, spaceId) || isClosedBySpace(shift);

  const allClosed = shifts.every(isClosed);
  const allClosedByRules =
    shifts.length > 0 && shifts.every((s) => closedByOtherRules(s, spaceId));

  const stateOf = (shiftId: string): SegmentState => {
    const shift = shifts.find((s) => s.id === shiftId);
    return shift && isClosed(shift) ? "locked" : "free";
  };

  const toggleShift = (shiftId: string) => {
    const shift = shifts.find((s) => s.id === shiftId);
    if (!shift || loading) return;
    onToggle(!isClosedBySpace(shift), [shiftId]);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Bereich sperren"
      maxWidth="sm"
      fullWidth
      actions={[
        {
          id: "fullDay",
          label: allClosed ? "Ganzer Tag freigeben" : "Ganzer Tag sperren",
          alignLeft: true,
          disabled: loading || !range || (allClosedByRules && !shifts.some(isClosedBySpace)),
          // No shiftIds: every shift of the day, same as the lock without the
          // flag, so the lock icon and the table markers agree afterwards.
          onClick: () => onToggle(!allClosed),
        },
        { id: "done", label: "Schließen", onClick: onClose },
      ]}
    >
      <Typography variant="text-3" color="subdued" className="mg-bt-sm">
        Schicht antippen zum Sperren, gesperrte Schicht antippen zum Aufheben.
      </Typography>
      {!range ? (
        <Typography variant="text-3">
          Für diesen Tag gibt es keine Schicht, die diesen Bereich bespielt.
        </Typography>
      ) : (
        <ShiftBlockTimeline
          segments={segments}
          range={range}
          stateOf={stateOf}
          onToggle={toggleShift}
          isDisabled={(id) => {
            const shift = shifts.find((s) => s.id === id);
            return loading || (!!shift && closedByOtherRules(shift, spaceId) && !isClosedBySpace(shift));
          }}
          titleOf={(id) => {
            const shift = shifts.find((s) => s.id === id);
            return shift && closedByOtherRules(shift, spaceId)
              ? "Durch andere Regeln geschlossen"
              : undefined;
          }}
          showPendingLegend={false}
        />
      )}
    </Modal>
  );
};

export default SpaceBlockModal;
